import { useState } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { useOrganization, useUser } from '@clerk/react';
import { GoogleSheetsIcon } from '@/components/ui/icons/google-product-icons';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardLinkButton,
  CardMeta,
  CardStatus,
} from '@/components/agent-chat/card-parts.component';
import { followingUserText } from '@/components/agent-chat/question-card.util';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAiChatContext,
  selectAiChatConversationId,
  selectAiChatSessions,
  sendAiChatMessage,
} from '@/store/features/ui.feature';
import { ConfigSelect } from '@/containers/playbooks/custom-builder/config/primitives';
import { useSheetSelection } from '@/containers/playbooks/custom-builder/config/use-sheet-selection.hook';
import type { SheetDraft } from '@/containers/playbooks/custom-builder/config/sheet-trigger.util';

const SHEETS_LOGO = (
  <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background'>
    <GoogleSheetsIcon className='size-[12px]' />
  </div>
);

/** The existing Sheet card's agent-trigger mode, scoped to this current chat. */
export function SheetTriggerPickCard({
  result,
  toolCallId,
}: {
  result: Record<string, unknown>;
  toolCallId: string;
}) {
  const context = useAppSelector(selectAiChatContext);
  const conversation = useAppSelector(selectAiChatConversationId);
  const sessions = useAppSelector(selectAiChatSessions);
  const { organization, isLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  const { unlocked } = useAgentsAccess();
  // Two plain values, not one object: useAuiState is useSyncExternalStore,
  // and a new object on every read never settles ("Maximum update depth").
  const state = {
    message: useAuiState((aui) => aui.message.id),
    available: useAuiState((aui) => {
      const tools = aui.message.content.filter(
        (part) => part.type === 'tool-call'
      );
      return (
        followingUserText(aui.thread.messages, aui.message.id) === null &&
        aui.thread.messages.at(-1)?.id === aui.message.id &&
        tools.at(-1)?.toolCallId === toolCallId
      );
    }),
  };
  const enabled =
    unlocked &&
    isLoaded &&
    userLoaded &&
    !!organization &&
    !!user &&
    context?.type === 'agent' &&
    !!context.agent_id &&
    result.agent_id === context.agent_id;
  if (!enabled) return null;
  const scope = JSON.stringify([
    organization.id,
    user.id,
    context.agent_id,
    conversation,
    sessions?.activeKey,
    state.message,
    toolCallId,
    result.spreadsheet_id,
    result.gid,
    state.available,
  ]);
  return (
    <PickedSheetCard
      key={scope}
      scope={scope}
      enabled={state.available}
      result={result}
    />
  );
}

function PickedSheetCard({
  scope,
  result,
  enabled,
}: {
  scope: string;
  result: Record<string, unknown>;
  enabled: boolean;
}) {
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState<SheetDraft>({});
  const [sent, setSent] = useState(false);
  const selection = useSheetSelection({
    config: draft,
    onChange: setDraft,
    caller: scope,
    enabled: enabled && !sent,
  });
  const sheet = draft.sheet;
  const requestedId =
    typeof result.spreadsheet_id === 'string' &&
    /^[A-Za-z0-9_-]{10,200}$/.test(result.spreadsheet_id)
      ? result.spreadsheet_id
      : undefined;
  const requestedGid =
    typeof result.gid === 'number' &&
    Number.isInteger(result.gid) &&
    result.gid >= 0
      ? result.gid
      : undefined;
  const usable =
    !!sheet?.spreadsheetId &&
    Number.isInteger(sheet.sheetId) &&
    (sheet.sheetId ?? -1) >= 0;
  /** False unlocks the button: nothing was sent. */
  const send = () => {
    if (
      !enabled ||
      sent ||
      selection.busy ||
      !selection.enabled ||
      !selection.canPick ||
      !usable
    )
      return false;
    setSent(true);
    const name = (sheet.spreadsheetName || 'Google Sheet')
      .replace(/\s+/g, ' ')
      .trim();
    const tab = (sheet.sheetName || 'Selected tab').replace(/\s+/g, ' ').trim();
    dispatch(
      sendAiChatMessage(
        `Picked ${name}, tab ${tab}: https://docs.google.com/spreadsheets/d/${sheet.spreadsheetId}/edit#gid=${sheet.sheetId}`
      )
    );
    return true;
  };
  // The card moves on by its own state once a tab is read; the button
  // unlocks either way, so a closed Picker can be opened again.
  const choose = async () => {
    await selection.choose(requestedId, requestedGid);
    return false;
  };
  const title = sheet?.spreadsheetName || 'Pick a Google Sheet';
  const locked = selection.busy || !selection.canPick;
  const metadata = selection.metadata;
  const error = selection.error ? (
    <div role='alert'>
      <CardMeta tone='danger'>{selection.error}</CardMeta>
    </div>
  ) : null;
  if (sent || !enabled)
    return (
      <AgentCard
        data-sheet-trigger-pick
        leading={SHEETS_LOGO}
        title={title}
        trailing={
          sent ? (
            <CardStatus tone='success'>Selected</CardStatus>
          ) : (
            <CardStatus>Passed over</CardStatus>
          )
        }
      >
        {error}
      </AgentCard>
    );
  return (
    <AgentCard data-sheet-trigger-pick leading={SHEETS_LOGO} title={title}>
      {usable ? (
        <>
          {metadata ? (
            <CardMeta>
              {metadata.rows.toLocaleString()}{' '}
              {metadata.rows === 1 ? 'row' : 'rows'}, {metadata.columns.length}{' '}
              {metadata.columns.length === 1 ? 'column' : 'columns'}
            </CardMeta>
          ) : null}
          <CardFooter
            key='use'
            links={
              <CardLinkButton
                disabled={locked}
                onClick={() => void selection.choose(requestedId, requestedGid)}
              >
                Change
              </CardLinkButton>
            }
            control={
              <div className='min-w-0 flex-1'>
                <ConfigSelect
                  ariaLabel='Trigger sheet tab'
                  value={String(sheet.sheetId)}
                  disabled={locked}
                  options={
                    metadata?.tabs.map((tab) => ({
                      value: String(tab.sheetId),
                      label: tab.sheetName,
                    })) ?? [
                      {
                        value: String(sheet.sheetId),
                        label: sheet.sheetName || 'Selected tab',
                      },
                    ]
                  }
                  onChange={(gid) =>
                    void selection.load(sheet.spreadsheetId!, Number(gid))
                  }
                />
              </div>
            }
            primary={{
              label: 'Use this tab',
              onClick: send,
              disabled: locked,
            }}
          />
        </>
      ) : (
        <CardFooter
          key='pick'
          control={
            <CardMeta className='min-w-0'>
              {selection.canPick
                ? 'Opens with Google'
                : 'Google Sheets not connected'}
            </CardMeta>
          }
          primary={{
            label: 'Pick a sheet',
            workingLabel: 'Reading',
            onClick: choose,
            disabled: locked,
          }}
        />
      )}
      {error}
    </AgentCard>
  );
}
